import { useState, type ReactNode } from "react";
import { makePostApiCall } from "../../shared/makeApiCall";
import ModelErrorComponent from "../../components/modals/ModelErrorComponent";
import type { ApiErrorPayload } from "../../types/api";
import type { CartItem, CartOrderDetails, CreatedOrder, CreateOrderPayload } from "../../types/orders";
import { validateOrderProducts } from "../../shared/orderAvailability";
import { buildCreateOrderPayload } from "../../shared/orderPayloads";

interface ModalState { open: boolean; childComponent?: ReactNode }
interface CreateFromCartInput { cart: CartItem[]; orderDetails: CartOrderDetails; notes?: string | null }

export function CartViewModel(
  setCart: (cart: CartItem[]) => void,
  setOrderDetails: (details: CartOrderDetails | null) => void,
  logout: () => void,
) {
  const [emptyModal, setEmptyModelOpen] = useState<ModalState>({ open: false, childComponent: null });

  const showErrorModal = (title = "Unable to create order", subtitle?: string) => {
    setEmptyModelOpen({
      childComponent: (
        <ModelErrorComponent
          setOpen={() => setEmptyModelOpen({ open: false, childComponent: null })}
          subtitle={subtitle}
          title={title}
        />
      ),
      open: true,
    });
  };

  const createOrderFromCart = async ({ cart, orderDetails, notes }: CreateFromCartInput) => {
    if (orderDetails.customer_id == null) {
      showErrorModal("Customer is required");
      return;
    }
    const availability = await validateOrderProducts(
      orderDetails.customer_id,
      cart.map((item) => item.id),
    );
    if (!availability.success) {
      if (availability.errorStatus === 401) logout();
      const error = availability.data as ApiErrorPayload;
      showErrorModal(error.message ?? error.title, error.subtitle);
      return;
    }
    if (availability.data.unavailable_products.length > 0) {
      const availableIds = new Set(availability.data.available_product_ids);
      setCart(cart.filter((item) => availableIds.has(item.id)));
      showErrorModal(
        "Unavailable products were removed",
        availability.data.unavailable_products.map((item) => item.product_name).join(", "),
      );
      return;
    }

    const payload = buildCreateOrderPayload(
      cart,
      { ...orderDetails, customer_id: orderDetails.customer_id },
      notes,
    );
    const result = await makePostApiCall<CreatedOrder, CreateOrderPayload>("createOrder", payload);
    if (result.success) {
      setCart([]);
      setOrderDetails(null);
      window.location.href = `/orders/${result.data.id}`;
      return;
    }
    if (result.errorStatus === 401) logout();
    const error = result.data as ApiErrorPayload;
    showErrorModal(error.message ?? error.title, error.subtitle);
  };

  const revalidateCart = async (cart: CartItem[], orderDetails: CartOrderDetails | null) => {
    if (!orderDetails?.customer_id || cart.length === 0) return;
    const result = await validateOrderProducts(orderDetails.customer_id, cart.map((item) => item.id));
    if (!result.success) {
      if (result.errorStatus === 401) logout();
      return;
    }

    if (result.data.unavailable_products.length > 0) {
      const availableIds = new Set(result.data.available_product_ids);
      setCart(cart.filter((item) => availableIds.has(item.id)));
      showErrorModal(
        "Unavailable products were removed",
        result.data.unavailable_products.map((item) => item.product_name).join(", "),
      );
    }
  };

  return { emptyModal, setEmptyModelOpen, createOrderFromCart, revalidateCart };
}
